import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDeviceType, useIntersectionObserver } from '@vfit/shared/hooks';
import {
  volaModalManager,
  errorManager,
  ErrorService,
  trackLink,
  getTemplateData,
} from '@vfit/shared/data-access';
import { CoverageToolModal } from '@vfit/consumer/components';
import { Button, CustomText } from '@vfit/shared/atoms';
import {
  ICmsConfigAutoDesc,
  useAutoDesc,
  removeCurrency,
  usePartnerOfferFlow,
  useRedirectCoverageToolFlow,
} from '@vfit/consumer/hooks';
import {
  checkIsApp,
  checkPanicMode,
  CONSUMER_CMS_DEFAULT,
  getClickToCallUrlByActionType,
  getFormattedPriceString,
  getProductCategory,
  getRedirectCoverage,
  IGlobalConfigCMS,
  IN_ACTIVATION,
  IProduct,
  isEnergyProduct,
  IWinBackCampaignConfig,
  IWinBackCMS,
  redirectToShoppingCartFromReverseAddress,
  useCmsConfig,
  usePage,
} from '@vfit/consumer/data-access';
import { ISupportModule, ProductDetail, StickyOffer, VfModal } from '@vfit/shared/components';
import { fonts } from '@vfit/shared/themes';
import {
  IActionSlide,
  IActionType,
  IClickToCallMeData,
  IErrorFormApi,
  ISubmitOutput,
  IType,
} from '@vfit/shared/models';
import {
  API,
  IGetAllCmsForm,
  manageErrorLeadPlatform,
  manageSuccessLeadPlatform,
  openLeadModal,
  openPopup,
} from '@vfit/shared/data-access';
import { useQueryClient } from 'react-query';
import { energyManager, SoftManagerService } from '@vfit/consumer/providers';
import { useRouter } from 'next/router';
import {
  ModalButtonContainer,
  ModalTitle,
  ModalContent,
  StickyOfferContainerTopProduct,
} from './topProduct.style';
import { IProductModal, ITopProductProps } from './topProduct.models';
import { checkProductOfferFlow } from './topProduct.utils';
import TopNavigation from './components/TopNavigation/topNavigation';
import TopNavigationLite from './components/TopNavigationLite/topNavigationLite';

const TopProduct = (props: ITopProductProps) => {
  const { product: topProduct, isR02, onOverrideCoverageToolCta } = props || {};
  const { push } = useRouter();
  const { cmsId } = topProduct;
  const queryClient = useQueryClient();
  const { page, silentLoginUser } = usePage();
  const winbackCampaignsList = useCmsConfig(
    CONSUMER_CMS_DEFAULT,
    API.CMS_GET_WINBACK_CONFIG
  ) as IWinBackCampaignConfig;
  const cmsWinbackData = useCmsConfig(
    CONSUMER_CMS_DEFAULT,
    API.CMS_GET_WINBACK_DATA
  ) as IWinBackCMS;
  const { globalconfig } =
    (useCmsConfig(CONSUMER_CMS_DEFAULT, API.CMS_GET_GLOBAL_CONFIG) as IGlobalConfigCMS) || {};
  const { panicMode } = globalconfig || {};
  const volaClickToCallData = useCmsConfig(
    CONSUMER_CMS_DEFAULT,
    API.CMS_GET_CLICK_TO_CALL_CONF
  ) as IClickToCallMeData;
  const ctcAutoDesc = useCmsConfig(CONSUMER_CMS_DEFAULT, API.CMS_GET_CTC_AUTO_DESC);
  const { enableAutoDesc, customString } =
    (ctcAutoDesc?.['ctcautodesc'] as ICmsConfigAutoDesc) || {};

  const { isSuccessPartnerOffer } = usePartnerOfferFlow({ product: topProduct });

  const product: IProduct = checkProductOfferFlow(topProduct, winbackCampaignsList, cmsWinbackData);
  const baseCtcUrl =
    product?.ctcPdpUrl || product?.pdpSecondAction?.callMeNow || product?.pdpSecondAction?.url;
  const iadd_auto_ctc = useAutoDesc(product as IProduct, {
    enableAutoDesc,
    url: baseCtcUrl,
    customString,
  });

  const [isShowCoverageTool, setIsShowCoverageTool] = useState(false);
  const [directionSlideStickyOffer, setDirectionSlideStickyOffer] = useState<
    'upward' | 'downward' | undefined
  >();
  const [isInActivation] = useRedirectCoverageToolFlow(cmsId || '');
  const [modal, setModal] = useState<IProductModal>({
    show: false,
    type: IType.TEXT,
  });
  const supportModulesCms = useCmsConfig(
    CONSUMER_CMS_DEFAULT,
    API.CMS_GET_SUPPORT_MODULE
  ) as ISupportModule;
  const slideProductMainBtn = useRef<HTMLDivElement>(null);
  const { isIntersecting: slideProductMainBtnIsIntersected } =
    useIntersectionObserver(slideProductMainBtn, { rootMargin: '-10%' }) || {};
  const { isDesktop } = useDeviceType();

  const productOfferTitle = getFormattedPriceString(product.price, product.recurrence);
  const detailLabel = product?.buttons?.[0]?.label || product?.buttonShow || '';
  const buttonAction = product?.action;

  useEffect(() => {
    if (!isShowCoverageTool && isInActivation) {
      setIsShowCoverageTool(true);
    }
  }, [isInActivation]);

  const resetModal = () => {
    setModal({
      show: false,
      type: IType.TEXT,
      text: '',
      title: '',
      disableOrganizeText: false,
    });
  };

  const onActionProduct = (action?: IActionSlide) => {
    let allLeads;
    let refLead;
    let lead;
    let autoActionUrlOverride;
    let finalAction;
    let pstEnrgEvent;
    switch (action?.type) {
      case IActionType.CUSTOM_URL:
        if (isR02?.() && product?.redirectR02) {
          window.open(product.redirectR02 || '', action?.isBlank === 'true' ? '_blank' : '_self');
        } else if (action?.url) {
          window.open(action.url, action?.isBlank === 'true' ? '_blank' : '_self');
        }
        break;
      case IActionType.CALL_ME_NOW:
        autoActionUrlOverride = (iadd_auto_ctc?.ctcAutoUrl ||
          product?.ctcPdpUrl ||
          action?.callMeNow) as string;
        finalAction = checkIsApp()
          ? autoActionUrlOverride
          : product?.ctcPdpUrl || action?.callMeNow;
        if (finalAction) openPopup(finalAction);
        break;
      case IActionType.CALL_ME_NOW_SUNRISE:
        if (action?.title && action?.url) {
          if (action?.template && action?.template !== '') {
            const templateData = getTemplateData(action?.template, queryClient);
            volaModalManager.handleVolaModal({
              url: getClickToCallUrlByActionType(action, product) || '',
              cmsClickToCallData: { ...templateData },
              taggingOption: page?.params?.elements?.taggingOptions,
              pageProduct: SoftManagerService(queryClient).getPageProduct(product),
            });
          } else {
            volaModalManager.handleVolaModal({
              url: getClickToCallUrlByActionType(action, product) || '',
              cmsClickToCallData: { ...volaClickToCallData },
              taggingOption: page?.params?.elements?.taggingOptions,
              pageProduct: SoftManagerService(queryClient).getPageProduct(product),
            });
          }
        }
        break;
      case IActionType.COVERAGE_TOOL:
        if (onOverrideCoverageToolCta) onOverrideCoverageToolCta();
        else setIsShowCoverageTool(true);
        break;
      case IActionType.CHECKOUT_MODAL_MOBILE:
        localStorage.setItem('tProd', JSON.stringify(product));
        window.location.href = getRedirectCoverage();
        break;
      case IActionType.COVERAGE_TOOL_WITH_REDIRECT:
        if (action?.url && cmsId) {
          const alreadyHaveParams = action.url.includes('?');
          const redirectUrl = alreadyHaveParams
            ? `${action.url}&${IN_ACTIVATION}=${cmsId}`
            : `${action.url}?${IN_ACTIVATION}=${cmsId}`;
          window.location.href = redirectUrl;
        }
        break;
      case IActionType.LEAD_MODAL:
        allLeads = queryClient?.getQueryData('getAllLeads') as IGetAllCmsForm;
        // form name
        refLead = action?.localUrl || '';
        lead = allLeads[refLead];
        if (lead?.forms) {
          openLeadModal({
            form: lead.forms,
            onSuccess: (submitOutput: ISubmitOutput) =>
              manageSuccessLeadPlatform(submitOutput, push, queryClient),
            onError: (submitOutput: ISubmitOutput[], errorCmsApi?: IErrorFormApi) =>
              manageErrorLeadPlatform(submitOutput, errorCmsApi, push, queryClient),
          });
        }
        break;
      case IActionType.CLICK_TO_CALL_ENERGY:
        document.dispatchEvent(
          new CustomEvent('energyCtcNotificationEvent', {
            detail: {
              product,
              action: action,
              taggingOption: page?.params?.elements?.taggingOptions,
            },
          })
        );
        break;
      case IActionType.POST_ENRG:
        trackLink(action?.title || '');
        pstEnrgEvent = new CustomEvent('enrgServiceNotificationEvent', {
          detail: {
            nof: action?.localUrl,
          },
        });
        document.dispatchEvent(pstEnrgEvent);
        break;
      case IActionType.AUTO_COVERAGE:
        if (cmsId) redirectToShoppingCartFromReverseAddress(cmsId);
        break;
      case IActionType.ENERGY_FUNNEL:
        energyManager.handleEnergyManager({
          product,
          showModal: true,
        });
        break;
      default:
        break;
    }
  };

  const hideCoverageTool = () => {
    setIsShowCoverageTool(false);
  };

  const modalCustomFooter = () => (
    <ModalButtonContainer>
      <Button
        variant={'primary'}
        onClick={() => {
          resetModal();
          onActionProduct(
            checkPanicMode(panicMode, product) ? product.panicModeAction : buttonAction
          );
        }}
        name={`action_top_product_${product?.offerId?.toString() || ''}`}
      >
        {checkPanicMode(panicMode, product) ? product.panicModeAction?.title : buttonAction?.title}
      </Button>
    </ModalButtonContainer>
  );

  const modalSimpleText = () => (
    <>
      {modal.title && (
        <ModalTitle>
          <CustomText
            fontFamily={fonts.exbold}
            size={36}
            lineHeight={45}
            textAlign="left"
            text={modal.title}
          />
        </ModalTitle>
      )}
      {modal.text && (
        <ModalContent>
          <CustomText
            size={18}
            lineHeight={24}
            textAlign="left"
            text={modal.text}
            disableOrganizeText={modal.disableOrganizeText}
          />
        </ModalContent>
      )}
    </>
  );

  const checkIsLoadingActivation = () => {
    return buttonAction?.type === IActionType.ENERGY_FUNNEL && !silentLoginUser?.isReady;
  };

  const getPDPMainAction = () => {
    return {
      action: () =>
        onActionProduct(
          checkPanicMode(panicMode, product) ? product.panicModeAction : buttonAction
        ),
      label:
        (checkPanicMode(panicMode, product)
          ? product.panicModeAction?.title
          : buttonAction?.title) || '',
    };
  };

  const getPDPSecondAction = () => {
    if (isEnergyProduct(product) && checkPanicMode(panicMode, product)) return undefined;
    return {
      action: () => {
        onActionProduct(product.pdpSecondAction);
      },
      label: product?.pdpSecondAction?.title || '',
    };
  };

  useLayoutEffect(() => {
    if (isDesktop) {
      setDirectionSlideStickyOffer(slideProductMainBtnIsIntersected ? 'downward' : 'upward');
    }
  }, [isDesktop, slideProductMainBtnIsIntersected]);

  if (topProduct?.partnerOffer?.isPartnerOffer && !isSuccessPartnerOffer) return null;

  if (topProduct?.isTpoOffer && !topProduct?.forceCampaign) {
    errorManager.handleError(ErrorService.getSeverityErrorHigh(), {
      title: 'Ops',
      message: 'Si è verificato un problema. Riprovare in seguito',
      fullscreen: true,
      disableCloseModalOnClickAction: true,
    });
    return null;
  }

  const enableLiteUI = product?.enableLiteUI || false;
  const TopNavigationComponent = enableLiteUI ? TopNavigationLite : TopNavigation;

  return (
    <>
      <TopNavigationComponent
        {...props}
        onSetModal={(modalObj) => setModal(modalObj)}
        onShowCoverageTool={(isShow) => setIsShowCoverageTool(isShow)}
        isShowCoverageTool={isShowCoverageTool}
      />
      <VfModal
        height={563}
        isOpen={modal.show}
        handleClose={resetModal}
        customFooter={modal.type === IType.PRODUCT ? modalCustomFooter : undefined}
      >
        {modal.text ? (
          modalSimpleText()
        ) : (
          <ProductDetail
            trackingOpt={{
              event: ['ui_interaction'],
              event_label: 'offer details',
              opts: { event_category: 'users', event_action: 'click' },
              visitorTrackingOpts: SoftManagerService(queryClient).getUser(),
              disableInitialTrack: false,
            }}
            details={{
              title: product.title,
              conditions: product.conditions,
              callMeNowHelp: product.callMeNowHelp,
              description: product?.descriptionPdp || product?.description,
              offerDetails: product?.offerDetails,
              customDescriptionCVM: product.customDescriptionCVM,
              price: product.price,
              recurrence: product.recurrence,
            }}
            modalInfo={{
              title: supportModulesCms?.supportmodules?.needHelp?.title || '',
              ctaName: supportModulesCms?.supportmodules?.needHelp?.ctaSupportCall || '',
            }}
            showHelps={false}
          />
        )}
      </VfModal>
      {product?.price && buttonAction?.title && (
        <StickyOfferContainerTopProduct>
          <StickyOffer
            slideBanner={directionSlideStickyOffer}
            id="sticky-offer-product"
            title={productOfferTitle}
            description={detailLabel || ''}
            topLabel={product?.topLabelStickyPdp || ''}
            isLoadingGoNext={checkIsLoadingActivation()}
            ctaActivation={getPDPMainAction()}
            secondCta={getPDPSecondAction()}
            content={
              <ProductDetail
                trackingOpt={{
                  event: ['view'],
                  event_label: 'offer details',
                  opts: {
                    product_target_segment: 'consumer',
                    page_section: `${getProductCategory(product.offerType || '')} offers`,
                    page_subsection: 'product_page',
                  },
                  pageProduct: {
                    product_id: `${
                      product?.isLandingClub
                        ? product?.campaignIdLanding || product?.offerId
                        : product?.offerId
                    }`,
                    product_name: product?.slug,
                    product_category: getProductCategory(product?.offerType || ''),
                    product_price: product?.price && removeCurrency(product.price),
                    product_quantity: '1',
                  },
                }}
                details={{
                  title: product.title,
                  conditions: product.conditions,
                  callMeNowHelp: product.callMeNowHelp,
                  description: product?.descriptionPdp || product?.description,
                  offerDetails: product?.offerDetails,
                  originalPrice: product.originalPrice,
                  price: product.price,
                  customDescriptionCVM: product.customDescriptionCVM,
                  recurrence: product.recurrence,
                }}
                modalInfo={{
                  title: supportModulesCms?.supportmodules?.needHelp?.title || '',
                  ctaName: supportModulesCms?.supportmodules?.needHelp?.ctaSupportCall || '',
                }}
              />
            }
            hideDetailLabel={product?.buttonHide}
            showDetailLabel={product?.buttonShow}
            enableLiteUI={enableLiteUI}
          />
        </StickyOfferContainerTopProduct>
      )}
      {product && (
        <CoverageToolModal
          backgroundImage={product.image}
          isOpen={isShowCoverageTool}
          product={product}
          handleClose={hideCoverageTool}
        />
      )}
    </>
  );
};
export default TopProduct;
